/* eslint-disable no-underscore-dangle */
import { Injectable } from "@angular/core";
import { HandleError, HttpErrorHandler, HttpUtility } from "@core/http";
import { IPageResult, ProductNames } from "@mynexus/mynexus-ui-lib";
import { Observable, catchError, of } from "rxjs";
import { WoundOstomyAdmissionAssignedClinician, WCCClinician } from "@app/core/model/wound-ostomy-admission.model";
import { WccRelevantSearchData, WoundOstomyAdmission,WCCAdmissionTask, WccApplicableRelevantSearchData} from "@core/model";
import { ShellService } from "@mynexus/mfe-core";

@Injectable({
  providedIn: 'root'
})
export class WoundCareConnectService {
  public handleError: HandleError;

  private _relevantSearchData?: WccRelevantSearchData;
  private readonly apiBaseUrl: string;

  constructor(
    private httpUtility: HttpUtility, 
    private httpErrorHandler: HttpErrorHandler,
    shellService: ShellService){
    this.handleError = this.httpErrorHandler.createHandleError('WoundCareConnectService');
    this.apiBaseUrl = shellService.getBaseUrlFor(ProductNames.HomeHealth);
  }

  public get relevantSearchData(): WccRelevantSearchData | undefined {
    return this._relevantSearchData;
  }

  public set relevantSearchData(data: WccRelevantSearchData | undefined) {
    this._relevantSearchData = data;
  }

  public getWoundCareAdmissions(tenantId: string | undefined, query: string = ''): Observable<IPageResult<WoundOstomyAdmission>> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyAdmissionExternal/$query`;

    return this.httpUtility
      .post<IPageResult<WoundOstomyAdmission>>(url, query)
      .pipe(
        catchError(this.handleError('getWoundCareAdmissions', {} as IPageResult<WoundOstomyAdmission>)));
  }

  public getStaffedWoundCareAdmissions(tenantId: string,query: string = ''): Observable<IPageResult<WoundOstomyAdmission>> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyAdmissionExternal/GetAdmissionsWithClinician/$query`;
    
    return this.httpUtility
      .post<IPageResult<WoundOstomyAdmission>>(url, query)
      .pipe(catchError(this.handleError('getStaffedWoundCareAdmissions', {} as IPageResult<WoundOstomyAdmission>)));      
  }

  public updateAssignedWccClinician(tenantId: string, assignedClinician: WoundOstomyAdmissionAssignedClinician, isClinicianReassign: boolean): Observable<WoundOstomyAdmissionAssignedClinician> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyClinicians/Update/isClinicianReassign/${isClinicianReassign}`;
    return this.httpUtility.put(url, assignedClinician);
  }   
    
  getCliniciansProviderAdmins(tenantId: string, providerAccountId: string): Observable<WCCClinician[]> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyClinicians/GetCliniciansByProviderAccountId/providerAccountId/${providerAccountId}`;
    return this.httpUtility.get(url, providerAccountId);
  }

  public getRelevantSearchData(tenantId: string): Observable<WccRelevantSearchData> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyAdmissionExternal/relevantSearchData`;
    
    if (this._relevantSearchData) {
      return of(this._relevantSearchData);
    }
 
    return this.httpUtility
      .get<WccRelevantSearchData>(url)
      .pipe(catchError(this.handleError('getRelevantSearchData', {} as WccRelevantSearchData)));
  }

  public getApplicableRelevantSearchData(tenantId: string, searchData: WccApplicableRelevantSearchData): Observable<WccRelevantSearchData> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyAdmissionExternal/applicableSearchData`;
  
    if (!searchData.query) {
      searchData.query = '?'
    }

    return this.httpUtility
      .post<WccRelevantSearchData>(url, searchData)
      .pipe(catchError(this.handleError('getApplicableRelevantSearchData', {} as WccRelevantSearchData)));
  }

  public getMyAdmissionsApplicableRelevantSearchData(tenantId: string, searchData: WccApplicableRelevantSearchData): Observable<WccRelevantSearchData> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyAdmissionExternal/myAdmissions/applicableSearchData`;

    if (!searchData.query) {
      searchData.query = '?'
    } 
       
    return this.httpUtility
      .post<WccRelevantSearchData>(url, searchData)
      .pipe(catchError(this.handleError('getApplicableRelevantSearchData', {} as WccRelevantSearchData)));
  }

  public getActiveTasksbyAdmissionId(tenantId: string, admissionId:string): Observable<WCCAdmissionTask[]>{
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyAdmissionExternal/GetWCCAdmissionActiveTasksExternal/${admissionId}`
    
    return this.httpUtility
    .get<WCCAdmissionTask[]>(url)
    .pipe(catchError(this.handleError('getActiveTasksbyAdmissionId', [] as WCCAdmissionTask[])));    
  }

  public getArchivedTasksbyAdmissionId(tenantId: string, admissionId:string): Observable<WCCAdmissionTask[]>{
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/WoundOstomyAdmissionExternal/GetWCCAdmissionArchivedTasksExternal/${admissionId}`

    return this.httpUtility
    .get<WCCAdmissionTask[]>(url)
    .pipe(catchError(this.handleError('getArchivedTasksbyAdmissionId', [] as WCCAdmissionTask[])));    
  }
}
